import React, { useEffect, useMemo, useState } from 'react'
import { useDrag } from 'react-dnd'
import { FormGroup, Row,Form} from 'react-bootstrap'
import { DropzoneAccepted } from './resources/DropzoneAccept'
import moment from 'moment'
import QRCode from 'qrcode.react'
import Barcode from 'react-barcode'
//import Radio from 'components/atoms/@vuexy/radio/RadioVuexy'
import { Checkbox, Radio,FormControlLabel } from '@mui/material'
//import { Check, Minus, Search, Save } from 'react-feather'
//import Checkbox from 'react-custom-checkbox'
import FeatherIcon from 'feather-icons-react'
import Select from 'react-select'
import parse from 'html-react-parser'
import './Box.scss'
import MonitorChart from '../VitalSign/MonitorChart'
import useAuth from 'hooks/useAuth'
import { applyTextEditorImageSource } from './textEditorImageBinding.util'
import { getReportRepeatPageSlice } from './reportPagination.util'
import { paginateReportGroups } from './reportGroupPagination.util'
import { getReportDesignerTextPlaceholder } from './reportDesignerText.util'

moment.locale('th')
moment.updateLocale('th', {
  months: [
    'มกราคม',
    'กุมภาพันธ์',
    'มีนาคม',
    'เมษายน',
    'พฤษภาคม',
    'มิถุนายน',
    'กรกฎาคม',
    'สิงหาคม.',
    'กันยายน',
    'ตุลาคม',
    'พฤศจิกายน',
    'ธันวาคม',
  ],
})

const style = {
  position: 'absolute',
  border: '0px dashed gray',
  backgroundColor: 'transparent',
  cursor: 'move',
  // whitepace : 'pre-wrap'
}

const REPORT_FONT_FAMILY = {
  angsana: 'Angsana New',
  Prompt: 'Prompt',
  sarabun: 'Sarabun',
  THSarabun: 'CKD_THSarabun',
  THSarabunNew: 'CKD_THSarabunNew',
}

const isPlainObject = (value) =>
  value !== null && typeof value === 'object' && !Array.isArray(value)

const getTextEditorDataSourceVariableName = (item = {}) =>
  `${item.dataSourceVariableName || ''}`.trim()

const getValueByPath = (source, path) => {
  if (!source || !path) return undefined

  return path.split('.').reduce((result, key) => {
    if (result === null || typeof result === 'undefined') return undefined

    return result[key]
  }, source)
}

const getTextEditorFormatOption = (formatName = '', formatValue = '') => ({
  name: `${formatName || ''}`.trim().toLowerCase(),
  value: `${formatValue || ''}`.trim(),
})

const getTextEditorDecimalPlaces = (value, fallback = 2) => {
  if (value === '') return fallback

  const parsed = Number(value)
  if (!Number.isInteger(parsed) || parsed < 0) return fallback

  return parsed
}

const formatTextEditorValue = (value, formatOption = {}) => {
  if (value === null || typeof value === 'undefined') return ''

  if (formatOption.name === 'currency') {
    return formatTextEditorAmount(
      value,
      getTextEditorDecimalPlaces(formatOption.value, 2),
    )
  }

  if (formatOption.name === 'number') {
    return formatTextEditorAmount(
      value,
      getTextEditorDecimalPlaces(formatOption.value, 0),
    )
  }

  if (formatOption.name === 'date') {
    return moment(value).format(formatOption.value || 'D MMMM YYYY')
  }

  if (moment.isMoment(value)) return value.format('D MMMM YYYY')
  if (value instanceof Date) return moment(value).format('D MMMM YYYY')
  if (Array.isArray(value)) return value.join(', ')
  if (isPlainObject(value)) return value.title || value.name || value.value || ''

  return value
}

const parseTextEditorNumber = (value) => {
  if (value === null || typeof value === 'undefined' || value === '') return null

  const parsed = Number(`${value}`.replace(/,/g, ''))
  return Number.isFinite(parsed) ? parsed : null
}

const formatTextEditorAmount = (value, decimalPlaces = 2) => {
  const parsed = parseTextEditorNumber(value)
  if (parsed === null) return value

  return parsed.toLocaleString('en-US', {
    minimumFractionDigits: decimalPlaces,
    maximumFractionDigits: decimalPlaces,
  })
}

const getTextEditorFallbackValue = (row = {}, fieldPath = '') => {
  const fieldName = fieldPath.split('.').pop() || ''
  const normalizedName = fieldName.toLowerCase()

  if (normalizedName === 'discount' || normalizedName === 'sumdiscount') {
    return '0.00'
  }

  if (normalizedName === 'netamount') {
    const amount = parseTextEditorNumber(row.amount)
    if (amount === null) return undefined

    const discount = parseTextEditorNumber(row.discount) || 0
    return formatTextEditorAmount(amount - discount)
  }

  if (normalizedName === 'sumnetamount') {
    const amount = parseTextEditorNumber(row.sumAmount)
    if (amount === null) return undefined

    const discount = parseTextEditorNumber(row.sumDiscount) || 0
    return formatTextEditorAmount(amount - discount)
  }

  return undefined
}

const getTextEditorDataSourceMap = (dataSourceList = []) =>
  (dataSourceList || []).reduce((result, item) => {
    const variableName = getTextEditorDataSourceVariableName(item)
    if (!variableName) return result

    const fieldValue = item?.fieldVariable?.value
    const rows = Array.isArray(fieldValue)
      ? fieldValue
      : isPlainObject(fieldValue)
      ? [fieldValue]
      : []

    result[variableName] = {
      item,
      rows: rows.filter(isPlainObject),
    }

    return result
  }, {})

const getFirstRowContext = (dataSourceMap = {}) =>
  Object.keys(dataSourceMap).reduce((result, variableName) => {
    const firstRow = dataSourceMap[variableName].rows[0] || {}
    result[variableName] = firstRow

    Object.keys(firstRow).forEach((fieldName) => {
      if (!Object.prototype.hasOwnProperty.call(result, fieldName)) {
        result[fieldName] = firstRow[fieldName]
      }
    })

    return result
  }, {})

const replaceTextEditorTokens = (text = '', context = {}, options = {}) =>
  `${text || ''}`.replace(
    /\{([A-Za-z_][A-Za-z0-9_]*)\.([A-Za-z_][A-Za-z0-9_.-]*)(?:\|([A-Za-z_][A-Za-z0-9_-]*)(?::([^}]*))?)?\}/g,
    (match, variableName, fieldPath, formatName, formatValue) => {
      if (!Object.prototype.hasOwnProperty.call(context, variableName)) {
        return options.keepMissingTokens ? `{${fieldPath}}` : match
      }

      const row = context[variableName]
      const value = getValueByPath(row, fieldPath)
      const fallbackValue =
        value === null || typeof value === 'undefined'
          ? getTextEditorFallbackValue(row, fieldPath)
          : undefined
      const nextValue =
        typeof fallbackValue === 'undefined' ? value : fallbackValue

      if (options.keepMissingTokens &&
        (nextValue === null || typeof nextValue === 'undefined')) {
        if (row?.__repeatPlaceholderRow) return ''
        return `{${fieldPath}}`
      }

      return formatTextEditorValue(
        nextValue,
        getTextEditorFormatOption(formatName, formatValue),
      )
    },
  ).replace(
    /\{([A-Za-z_][A-Za-z0-9_-]*)(?:\|([A-Za-z_][A-Za-z0-9_-]*)(?::([^}]*))?)?\}/g,
    (match, fieldName, formatName, formatValue) => {
      if (!Object.prototype.hasOwnProperty.call(context, fieldName)) {
        return match
      }

      const value = context[fieldName]

      if (options.keepMissingTokens &&
        (value === null || typeof value === 'undefined')) {
        return match
      }

      return formatTextEditorValue(
        value,
        getTextEditorFormatOption(formatName, formatValue),
      )
    },
  )

const TEXT_EDITOR_TOKEN_ATTRIBUTE_NAMES = ['src', 'alt', 'title']

const replaceTextTokensInAttributes = (
  root,
  context = {},
  options = {},
) => {
  const elements = Array.from(root?.querySelectorAll?.('*') || [])

  elements.forEach((element) => {
    TEXT_EDITOR_TOKEN_ATTRIBUTE_NAMES.forEach((attributeName) => {
      if (!element.hasAttribute(attributeName)) return

      const attributeValue = element.getAttribute(attributeName)
      const nextAttributeValue = replaceTextEditorTokens(
        attributeValue,
        context,
        options,
      )
      const handledImageSource = applyTextEditorImageSource({
        attributeName,
        attributeValue,
        element,
        keepMissingTokens: options.keepMissingTokens,
        nextAttributeValue,
      })

      if (handledImageSource) return

      if (nextAttributeValue !== attributeValue) {
        element.setAttribute(attributeName, nextAttributeValue)
      }
    })
  })
}

const replaceTextTokensInNode = (root, context = {}, options = {}) => {
  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT, null)
  let textNode = walker.nextNode()

  while (textNode) {
    textNode.nodeValue = replaceTextEditorTokens(
      textNode.nodeValue,
      context,
      options,
    )
    textNode = walker.nextNode()
  }

  replaceTextTokensInAttributes(root, context, options)
}

const removeTextEditorStyleProperties = (styleText = '', propertyNames = []) => {
  const normalizedNames = propertyNames.map((name) => name.toLowerCase())

  return `${styleText || ''}`
    .split(';')
    .map((item) => item.trim())
    .filter((item) => {
      const propertyName = item.split(':')[0]?.trim().toLowerCase()
      return item && !normalizedNames.includes(propertyName)
    })
    .join('; ')
}

const setTextEditorStyleAttribute = (node, styleText = '') => {
  if (styleText) {
    node.setAttribute('style', styleText)
    return
  }

  node.removeAttribute('style')
}

const appendTextEditorStyleProperties = (styleText = '', properties = {}) => {
  const propertyNames = Object.keys(properties)
  const currentStyle = removeTextEditorStyleProperties(styleText, propertyNames)
  const nextStyle = propertyNames
    .map((propertyName) => `${propertyName}: ${properties[propertyName]}`)
    .join('; ')

  return [currentStyle, nextStyle].filter(Boolean).join('; ')
}

const getTextEditorStyleProperty = (styleText = '', propertyName = '') => {
  const normalizedName = propertyName.toLowerCase()
  const styleItem = String(styleText || '')
    .split(';')
    .map((item) => item.trim())
    .find((item) => item.split(':')[0]?.trim().toLowerCase() === normalizedName)

  return styleItem ? styleItem.split(':').slice(1).join(':').trim() : ''
}

const getTextEditorTableColgroup = (table) =>
  Array.from(table?.children || []).find((child) => child.nodeName === 'COLGROUP')

const getTextEditorColgroupColumns = (colgroup) =>
  Array.from(colgroup?.children || []).filter((child) => child.nodeName === 'COL')

const getTextEditorNodeWidthText = (node) =>
  getTextEditorStyleProperty(node?.getAttribute?.('style'), 'width') ||
  `${node?.getAttribute?.('width') || ''}`.trim()

const getTextEditorPixelWidthAmount = (width) => {
  const match = `${width || ''}`.trim().match(/^([0-9.]+)(px)?$/i)
  if (!match) return null

  const amount = Number(match[1])
  return Number.isFinite(amount) && amount > 0 ? amount : null
}

const formatTextEditorPercentWidth = (width) => {
  const rounded = Math.round(width * 10000) / 10000
  return `${rounded}`.replace(/\.0+$/, '').replace(/(\.\d*?)0+$/, '$1') + '%'
}

const normalizeTextEditorTableColumnWidths = (template) => {
  const tables = Array.from(template.content.querySelectorAll('table'))

  tables.forEach((table) => {
    const columns = getTextEditorColgroupColumns(getTextEditorTableColgroup(table))
    const widths = columns.map((column) =>
      getTextEditorPixelWidthAmount(getTextEditorNodeWidthText(column)),
    )

    if (!widths.length || widths.some((width) => !width)) return

    const total = widths.reduce((sum, width) => sum + width, 0)
    if (!total) return

    columns.forEach((column, index) => {
      const width = formatTextEditorPercentWidth((widths[index] / total) * 100)

      setTextEditorStyleAttribute(
        column,
        appendTextEditorStyleProperties(column.getAttribute('style'), {
          width,
        }),
      )
      column.setAttribute('width', width)
    })
  })
}

const resetTextEditorRepeatRowHeight = (row) => {
  setTextEditorStyleAttribute(
    row,
    removeTextEditorStyleProperties(row.getAttribute('style'), [
      'height',
      'min-height',
    ]),
  )

  Array.from(row.children || []).forEach((cell) => {
    if (!['TD', 'TH'].includes(cell.nodeName)) return

    setTextEditorStyleAttribute(
      cell,
      removeTextEditorStyleProperties(cell.getAttribute('style'), [
        'height',
        'min-height',
      ]),
    )
  })
}

const hideTextEditorRepeatBodyLines = (row) => {
  Array.from(row.children || []).forEach((cell) => {
    if (!['TD', 'TH'].includes(cell.nodeName)) return

    setTextEditorStyleAttribute(
      cell,
      appendTextEditorStyleProperties(cell.getAttribute('style'), {
        'border-top': '0',
        'border-bottom': '0',
      }),
    )
  })
}

const applyTextEditorRepeatRowOptions = (row, repeatRow) => {
  if (!repeatRow.getAttribute('data-row-height')) {
    resetTextEditorRepeatRowHeight(row)
  }

  if (repeatRow.getAttribute('data-repeat-body-lines') === 'hidden') {
    hideTextEditorRepeatBodyLines(row)
  }
}

const preserveTextEditorPlaceholderRowHeight = (row) => {
  Array.from(row.children || []).forEach((cell) => {
    if (!['TD', 'TH'].includes(cell.nodeName)) return
    if (`${cell.textContent || ''}`.trim()) return

    cell.appendChild(cell.ownerDocument.createTextNode('\u00a0'))
  })
}

const isTextEditorLastPage = (options = {}) => {
  const pageNo = parseInt(options.pageNo, 10) || 0
  const totalPage = parseInt(options.totalPage, 10) || 0

  if (pageNo > 0 && totalPage > 0) {
    return pageNo === totalPage
  }

  const repeatPageNo = parseInt(options.repeatPageNo, 10) || 0
  const repeatTotalPage = parseInt(options.repeatTotalPage, 10) || 0

  return repeatPageNo > 0 &&
    repeatTotalPage > 0 &&
    repeatPageNo === repeatTotalPage
}

const isTextEditorFirstPage = (options = {}) => {
  const pageNo = parseInt(options.pageNo, 10) || 0

  if (pageNo > 0) return pageNo === 1

  const repeatPageNo = parseInt(options.repeatPageNo, 10) || 0

  return repeatPageNo <= 1
}

const hideTextEditorPageNode = (node) => {
  if (node.hasAttribute('data-preserve-space-on-hidden')) {
    node.style.visibility = 'hidden'
    node.removeAttribute('data-preserve-space-on-hidden')
    return
  }

  node.parentNode?.removeChild(node)
}

const applyTextEditorPageVisibility = (template, options = {}) => {
  const showFirstPageOnly = Array.from(
    template.content.querySelectorAll('[data-show-on-first-page]'),
  )
  const showLastPageOnly = Array.from(
    template.content.querySelectorAll('[data-show-on-last-page]'),
  )
  const isFirstPage = isTextEditorFirstPage(options)
  const isLastPage = isTextEditorLastPage(options)

  showFirstPageOnly.forEach((node) => {
    if (!isFirstPage) {
      hideTextEditorPageNode(node)
      return
    }

    node.removeAttribute('data-show-on-first-page')
    node.removeAttribute('data-preserve-space-on-hidden')
    node.classList.remove('report-first-page-only')
  })

  showLastPageOnly.forEach((node) => {
    if (!isLastPage) {
      hideTextEditorPageNode(node)
      return
    }

    node.removeAttribute('data-show-on-last-page')
    node.classList.remove('report-last-page-only')
  })
}

const renderTextEditorRepeatRows = (
  template,
  dataSourceMap,
  defaultContext,
  options = {},
) => {
  const repeatRows = Array.from(
    template.content.querySelectorAll('tr[data-repeat]'),
  )

  repeatRows.forEach((row) => {
    const variableName = `${row.getAttribute('data-repeat') || ''}`.trim()
    const dataSource = dataSourceMap[variableName]
    const rows = dataSource?.rows || []
    const minRows = parseInt(row.getAttribute('data-repeat-min-rows'), 10) || 0
    const repeatRowPerPage = parseInt(options.repeatRowPerPage, 10) || 0
    const repeatPageNo = Math.max(parseInt(options.repeatPageNo, 10) || 1, 1)
    const shouldPaginate = repeatRowPerPage > 0
    const pageSlice = getReportRepeatPageSlice({
      rowCount: rows.length,
      pageNo: repeatPageNo,
      rowPerPage: repeatRowPerPage,
      pageBreakAfterRows: options.repeatPageBreakAfterRows,
      lastPageRowPerPage: options.repeatLastPageRowPerPage,
      rowsOnFirstPageOnly: options.repeatRowsOnFirstPageOnly === true,
    })
    const pageStart = shouldPaginate ? pageSlice.start : 0
    const pageEnd = shouldPaginate ? pageSlice.end : undefined
    const displayRows = shouldPaginate
      ? rows.slice(pageStart, pageEnd)
      : rows.slice()
    const isEmptyForcedSummaryPage =
      pageSlice.totalPage > 1 &&
      repeatPageNo === pageSlice.totalPage &&
      pageStart === rows.length &&
      pageEnd === rows.length
    const repeatPageCapacity =
      repeatPageNo === pageSlice.totalPage
        ? parseInt(options.repeatLastPageRowPerPage, 10) || repeatRowPerPage
        : repeatRowPerPage
    const minimumRows =
      shouldPaginate && options.fillEmptyRows === true
        ? isEmptyForcedSummaryPage
          ? 0
          : repeatPageCapacity
        : minRows

    while (displayRows.length < minimumRows) {
      const nextRowNo = pageStart + displayRows.length + 1
      displayRows.push({
        __repeatPlaceholderRow: true,
        no: `${nextRowNo}`,
        rowNo: `${nextRowNo}`,
      })
    }

    if (!displayRows.length && options.keepMissingTokens) {
      applyTextEditorRepeatRowOptions(row, row)
      replaceTextTokensInNode(row, defaultContext, options)
      return
    }

    displayRows.forEach((record) => {
      const clone = row.cloneNode(true)
      const rowContext = Object.assign({}, defaultContext, {
        [variableName]: record,
      })

      applyTextEditorRepeatRowOptions(clone, row)
      clone.removeAttribute('data-repeat')
      clone.removeAttribute('data-repeat-source')
      clone.removeAttribute('data-repeat-body-lines')
      clone.removeAttribute('data-repeat-min-rows')
      clone.classList.remove('report-repeat-row')
      replaceTextTokensInNode(clone, rowContext, options)
      if (record.__repeatPlaceholderRow) {
        preserveTextEditorPlaceholderRowHeight(clone)
      }
      row.parentNode.insertBefore(clone, row)
    })

    row.parentNode.removeChild(row)
  })
}

export const renderTextEditorSourceCode = (
  sourceCode = '',
  dataSourceList = [],
  options = {},
) => {
  if (!sourceCode) return ''

  const dataSourceMap = getTextEditorDataSourceMap(dataSourceList)
  const defaultContext = getFirstRowContext(dataSourceMap)
  const hasDataSource = Object.keys(dataSourceMap).length > 0

  if (typeof document === 'undefined') {
    return hasDataSource
      ? replaceTextEditorTokens(sourceCode, defaultContext, options)
      : sourceCode
  }

  const template = document.createElement('template')
  template.innerHTML = sourceCode

  normalizeTextEditorTableColumnWidths(template)

  if (hasDataSource) {
    applyTextEditorPageVisibility(template, options)
    renderTextEditorRepeatRows(template, dataSourceMap, defaultContext, options)
    replaceTextTokensInNode(template.content, defaultContext, options)
  }

  return template.innerHTML
}
export const Box = ({
  id,
  left,
  top,
  width,
  height,
  hideSourceOnDrag,
  activeBackground,
  showBorder=false,
  children,
  item,
  onClick,
  onDoubleClick=()=>{},
  onChange,
  disabled,
  pageNo,
  totalPage,
  pageNoOfLayoutPage=1,totalPageOfLayoutPage=1,
  layouts,
  pdfOverlayMode = false,
  multiSelected=false,
  dataSourceList = [],
}) => {
  const [isStyleFromLayout, setIsStyleFromLayout] = useState(false)

  useMemo(() => {
    if (layouts) {
      let { color, fontWeight } = layouts
      if (color || fontWeight) {
        setIsStyleFromLayout(true)
      } else {
        setIsStyleFromLayout(false)
      }
    }
  }, [layouts])

    
  const styleFromLayout = {};

  if (layouts?.color) {
    styleFromLayout.color = layouts.color;
  }

  if (layouts?.fontWeight) {
    styleFromLayout.fontWeight = layouts.fontWeight;
  }  

  const isNumber = (value)=> {
    // Remove commas (for numbers like "1,300")
    if(value){
      const sanitizedValue = String(value).replace(/,/g, '');
    
      // Use parseFloat to parse the value and check if it's a valid number
      const parsedValue = parseFloat(sanitizedValue);
    
      // Check if the parsed value is a valid number and not NaN
      return !isNaN(parsedValue) && isFinite(parsedValue);
    }else{
      return false
    }
  }

  const convertCurrency = (amount)=>{
    //const amount = 1234567.89;
    if(isNumber(amount)){
      
      const formattedAmount = Number(amount).toLocaleString('th-TH', {
        minimumFractionDigits: 2 // This option hides the currency symbol
      });
     // console.log('isCu',amount,formattedAmount)
      return formattedAmount
    }else{
      return amount
    }
  }

  const isLocked = item?.locked === true
  const isBackground = item?.isBackground === true
  const designerTextPlaceholder =
    getReportDesignerTextPlaceholder(item, { disabled })

  const [{ isDragging }, drag] = useDrag(
    () => ({
      type: 'box',
      item: { ...item, id, left, top },
      canDrag: !disabled && !isLocked,
      collect: (monitor) => ({
        isDragging: monitor.isDragging(),
      }),
    }),
    [item, id, left, top, disabled, isLocked]
  )

  if (isDragging && hideSourceOnDrag) {
    return <div ref={drag} />
  }
  let bg = { backgroundColor: 'transparent' }
  if (activeBackground) {
    //bg = { backgroundColor: 'lightgray' }
  }
  if (disabled && disabled === true) {
    onClick = () => {}
    onChange = () => {}
  }

  const onDropZoneChange = (files) => {
    console.log('dzc', files)
  }

  const roughScale = (x, base) => {
    const parsed = parseInt(x, base)
    if (isNaN(parsed)) {
      return 0
    }
    return parsed
  }

  const toCssSize = (value) => {
    if (value === null || typeof value === 'undefined' || value === '') {
      return value
    }

    if (typeof value === 'number') {
      return `${value}px`
    }

    const stringValue = String(value).trim()

    if (/^[0-9.]+$/.test(stringValue)) {
      return `${stringValue}px`
    }

    if (/^([0-9.]+%)px$/i.test(stringValue)) {
      return stringValue.replace(/px$/i, '')
    }

    return stringValue
  }

  const safeUnescapeNewlines = (val: unknown): string =>
    typeof val === 'string' ? val.replace(/\\n/g, '\n').replace(/\\t/g, '\t') : '';  

  const hasMultilineValue = (val) =>
    typeof val === 'string' && (val.includes('\n') || val.includes('\\n'))

  const renderHeaderColumnDetail = (columns,fieldVariable,rowItem) =>{
    const extraClassName = ''
    return columns.map((citem, index) => {
                  //console.log('citem',citem,ritem)            
            const itemProps = fieldVariable?.props?.[citem]

            const level = itemProps.groupLevel || ''
            if (index<4) {

              
              return null;
            }
            
            return (<td key={index} style={{width:(fieldVariable?.props?.[citem].width?fieldVariable?.props?.[citem].width:'inherit')}}
            className={extraClassName+' '+(fieldVariable?.props?.[citem]?.textClass||'')}
            >                              
                <>{(itemProps?.isCurrency && itemProps?.isCurrency===true) ?convertCurrency(rowItem[citem]):safeUnescapeNewlines(rowItem[citem])}</>
              </td>)
          })
  }

  const currentPageIsFirst = pageNo && totalPage
    ? pageNo === 1
    : pageNoOfLayoutPage === 1
  const currentPageIsLast = pageNo && totalPage
    ? pageNo === totalPage
    : totalPageOfLayoutPage === pageNoOfLayoutPage
  const reportHasMultiplePages = pageNo && totalPage
    ? totalPage > 1
    : totalPageOfLayoutPage > 1

  const renderChildren = () => {
    if (designerTextPlaceholder) {
      return (
        <span
          data-report-designer-placeholder="true"
          data-pdf-overlay-designer-placeholder={pdfOverlayMode ? "true" : undefined}
          style={{
            display: 'block',
            width: '100%',
            height: '100%',
            minHeight: '10px',
            boxSizing: 'border-box',
            padding: '0 2px',
            color: '#0d6efd',
            backgroundColor: 'rgba(13, 110, 253, 0.08)',
            outline: '1px dashed rgba(13, 110, 253, 0.65)',
            fontSize: 'max(10px, 1em)',
            lineHeight: 'normal',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            pointerEvents: 'none',
          }}
        >
          {designerTextPlaceholder}
        </span>
      )
    }

    const showOnFirstPage = item.showOnFirstPage || false
    const showOnLastPage = item.showOnLastPage || false
    if(showOnFirstPage){
      if(!currentPageIsFirst){
        return null;
      }
    }
    if(showOnLastPage){
      if(!currentPageIsLast){
        return null;
      }
    }
    //console.log('render',item)
    if (item.type === 'text') {
      if(item.isCurrency && item.isCurrency===true){
        return convertCurrency(item.value)
      }
      return safeUnescapeNewlines(item.value)
    }else if (item.type === 'textformat') {     

      const result = item.format
        .replace('{page}', pageNo)
        .replace('{total}', totalPage)
        //.replace('{loginFullName}', user?.displayName || 'undefined');

      return safeUnescapeNewlines(result)      
    } else if (item.type == 'image') {
      return (
        <DropzoneAccepted
          item={item}
          disabled={disabled || isLocked}
          onChange={(file) => {
            onChange(file)
          }}
        />
      )
    } else if (item.type == 'pdf') {
      return (
        <DropzoneAccepted
          item={item}
          disabled={disabled || isLocked}
          onChange={(file) => {
            onChange(file)
          }}
        />
      )
    } else if (item.type == 'date') {
      return (
        <span>
          {moment()
            .add(543, 'year')
            .format(item.format ? item.format : null)}
        </span>
      )
    } else if (item.type == 'object_date') {
      return (
        <span>
          {moment(item.value)
            .add(543, 'year')
            .format(item.format ? item.format : null)}
        </span>
      )
    } else if (item.type == 'barcode') {
      return <Barcode value={item.value} width={width} height={height} displayValue={item.displayValue?item?.displayValue:false}/>
    } else if (item.type == 'qrcode') {
      return <QRCode value={item.value} size={width?parseInt(width, 10):width} />
    } else if (item.type == 'hline') {
      let style = {}
      if(item.borderStyle){
        style['borderStyle'] = item.borderStyle
      }
      return <hr className="hr-hline" width={width} style={style}/>
    } else if (item.type == 'vline') {
      let style = { height: item?.height }
      if(item.borderStyle){
        style['borderStyle'] = item.borderStyle
      }
      return (
        <div className="vline" width={1} style={style} />
      )
    } else if (item.type == 'choice') {
      if (Array.isArray(item.value)) {
        if (item.isHorizontal !== undefined && !item.isHorizontal) {
          return (
            <div>
              {item.value.map((vitem, vindex) => (
                <FormGroup row>
                  <Radio
                    color="primary"
                    label={vitem[Object.keys(vitem)[0]]}
                    icon={<FeatherIcon
                      size="12"
                      icon="check"
                      className="vx-icon"            
                    />}
                    checked={false}
                  />
                </FormGroup>
              ))}
            </div>
          )
        } else {
          return (
            <div>
              <Row className="align-items-center">
                {item.value.map((vitem, vindex) => (
                  <Radio
                    color="primary"
                    label={vitem[Object.keys(vitem)[0]]}
                    icon={<FeatherIcon
                      size="12"
                      icon="check"
                      className="vx-icon"            
                    />}
                    checked={false}
                  />
                ))}
              </Row>
            </div>
          )
        }
      }
      return (
        <div>
          <FormGroup row>
            <Radio
              color="primary"
              label={item.value}
              icon={<FeatherIcon
                size="12"
                icon="check"
                className="vx-icon"            
              />}
              checked={false}
            />
          </FormGroup>
        </div>
      )
    } else if (item.type == 'advance_table') {
      const columns =
        item.fieldVariable && item.fieldVariable.columns ? item.fieldVariable.columns : []
      const noOfColumn = columns ? columns.length : 4
      const boxWidth = 690 / noOfColumn

      const style = item.borderTop ? { borderTop: item.borderTop } : {}
      if (item.rowHeight) {
        let { rowHeight } = item
        if (rowHeight && (rowHeight + '').indexOf('px') > 0) {
          style.height = rowHeight
        } else {
          style.height = rowHeight + 'px'
        }
      }
      return (
        <div className="table table-border-dash" style={style}>
          <div className="table-head">
            {columns
              ? columns.map((citem, index) => {
                  const pItem =
                    item.fieldVariable.props && item.fieldVariable.props[citem]
                      ? item.fieldVariable.props[citem]
                      : {
                          id: index,
                          left: boxWidth * index,
                          top: 0,
                          width: boxWidth,
                          title: citem,
                          value: citem,
                          type: 'text',
                        }

                  return (
                    <Box
                      key={index}
                      {...pItem}
                      activeBackground={false}
                      hideSourceOnDrag={false}
                      item={pItem}
                      onClick={(item) => {}}
                      onChange={(item) => {}}
                      layouts={layouts}
                    ></Box>
                  )
                })
              : new Array(noOfColumn)
                  .fill('')
                  .map((item, index) => <div>Column{index}</div>)}
          </div>
        </div>
      )
      
  } else if (item.type == 'advance_table_result' && item?.renderUI === 'GROUP_LEVEL') {
    
    const groupPages = item.pageBreakGroupBy
      ? item._groupPages || paginateReportGroups(item)
      : null
    const groupPage = groupPages?.[pageNoOfLayoutPage - 1]
    const groupRowIndexes = groupPages ? new Set(groupPage?.rowIndexes || []) : null
    const continuedHeaderIndexes = new Set(groupPage?.continuedHeaderIndexes || [])

      const columns =
        item.fieldVariable && item.fieldVariable.columns ? item.fieldVariable.columns : []
      let noOfColumn = groupPages
        ? columns.filter((column) => item.fieldVariable?.props?.[column]?.groupDisplay !== true).length
        : columns ? columns.length : 4
      let prevGroupValues = {
          level1: null,
          level2: null,
          level3: null
        }
    let visualRowIndex = 0
      return (
        <table className="table-dynamic-report" style={groupPages ? { width: '100%', tableLayout: 'fixed' } : undefined}>
          {item.showHeader && item.showHeader === true && <thead>
            <tr>              
              {columns
                ? columns.map((citem, index) => (
                    <td>
                      {item.fieldVariable.props && item.fieldVariable.props[citem]
                        ? item.fieldVariable.props[citem].title
                        : citem}
                    </td>
                  ))
                : new Array(noOfColumn)
                    .fill('')
                    .map((item, index) => <td>Column{index}</td>)}
            </tr>
          </thead>}
          <tbody>
            {item.value && Array.isArray(item.value)
              ? item?.value?.reduce((rresult, ritem, rindex) => {

                    let rows = []
                    let hasRowTypeHeader = false
                    
                    item.columns.forEach((citem) => {

                      const itemProps = item.fieldVariable?.props?.[citem]

                      if (itemProps?.groupDisplay === true) {

                        const level = itemProps.groupLevel || 'level1'
                        const currentValue = ritem[citem]

                        if (prevGroupValues[level] !== currentValue) {
                          let contentHeaderTotal = null
                          // 🔥 RESET LOWER LEVEL
                          if (level === 'level1') {
                            prevGroupValues.level2 = null
                            prevGroupValues.level3 = null
                            if(ritem?.rowType && ritem['rowType']==='HEADER_TOTAL'){
                              noOfColumn = 1;
                              contentHeaderTotal = renderHeaderColumnDetail(item.columns,item.fieldVariable,ritem)
                              hasRowTypeHeader = true
                            }
                          }

                          if (level === 'level2') {
                            prevGroupValues.level3 = null
                          }

                          prevGroupValues[level] = currentValue

                          const extraClassName = (item.fieldVariable?.props?.[citem]?.textClass||'')
                          const fontSize = item.fieldVariable?.props?.[citem]?.fontSize;
                          const fontWeight = item.fieldVariable?.props?.[citem]?.fontWeight;
                          const textAlign = item.fieldVariable?.props?.[citem]?.textAlign;

                          const advStyle = {
                            ...(fontSize && { fontSize }),
                            ...(fontWeight && { fontWeight }),
                            ...(textAlign && { textAlign }),
                          };
                          const headerVisualIndex = visualRowIndex + rows.length
                          rows.push(
                            <tr
                              key={`group-${rindex}-${level}`}
                              style={advStyle}
                              className={`group-row ${level} ${hasRowTypeHeader && level=='level1'?'header-total':''} ${extraClassName}`}
                            >
                              <td colSpan={noOfColumn}>
                                {currentValue}
                                {level === 'level1' && continuedHeaderIndexes.has(headerVisualIndex) ? ' (ต่อ)' : ''}
                              </td>
                              {contentHeaderTotal}
                            </tr>
                          )
                        }
                      }
                    })

                    if(!hasRowTypeHeader){
                      
                      // ---------- DETAIL ROW ----------
                      rows.push(
                        <tr key={`detail-${rindex}`} className='group-row level-detail'>
                          
                          {item.columns.map((citem, index) => {
                            //console.log('citem',citem,ritem)
                            if(citem==='rowType'){
                              return null
                            }
                            if(item.fieldVariable?.props?.[citem]?.groupDisplay === true) return null

                            const itemProps=  item.fieldVariable?.props?.[citem]
                            
                            let extraClassName = ''
                            if(item.fieldVariable?.props?.[citem]?.rowTypeGroupTotalStyle === true && ritem?.rowType && ritem['rowType']==='GROUP_TOTAL'){
                              extraClassName = 'group-total'
                            }

                            return (<td key={index} style={{width:(item.fieldVariable?.props?.[citem].width?item.fieldVariable?.props?.[citem].width:'inherit')}}
                            className={extraClassName+' '+(item.fieldVariable?.props?.[citem]?.textClass||'')}
                            >                              
                                <>{(itemProps?.isCurrency && itemProps?.isCurrency===true) ?convertCurrency(ritem[citem]):safeUnescapeNewlines(ritem[citem])}</>
                              </td>)
                          })}
                        </tr>
                      )
                    }

                    //return rows
                  // ---------- PAGINATION (FIXED) ----------
                  const pageStart = item.rowPerPage
                    ? item.rowPerPage * (pageNoOfLayoutPage - 1)
                    : 0

                  const pageEnd = item.rowPerPage
                    ? item.rowPerPage * pageNoOfLayoutPage
                    : Infinity

                  rows.forEach(row => {
                    if (groupRowIndexes
                      ? groupRowIndexes.has(visualRowIndex)
                      : visualRowIndex >= pageStart && visualRowIndex < pageEnd) {
                    // calculate vitualRow count group level for row
                    //if (rindex >= pageStart && rindex < pageEnd) {
                      rresult.push(row)
                    }
                    visualRowIndex++
                  })
                  return rresult
                }, []
              )
              : new Array(4).fill('').map((item, rindex) => (
                  <tr>
                    {new Array(noOfColumn).fill('').map((item, cindex) => (
                      <td>
                        {rindex} - {cindex}
                      </td>
                    ))}
                  </tr>
                ))}
          </tbody>
        </table>
      )
    } else if (item.type == 'advance_table_result') {
      const columns =
        item.fieldVariable && item.fieldVariable.columns ? item.fieldVariable.columns : []
      const noOfColumn = columns ? columns.length : 4
      const boxWidth = 690 / noOfColumn
      let abStyle = item.borderTop ? { borderTop: item.borderTop } : {}
      height = item.rowHeight ? item.rowHeight : height
      if (height && (height + '').indexOf('px') > 0) {
        const list = height.match(/\d+/)
        height = list.length > 0 ? list[0] : 0
      }
      const pageSlice = getReportRepeatPageSlice({
        rowCount: item.value?.length || 0,
        pageNo: pageNoOfLayoutPage,
        rowPerPage: item.rowPerPage,
        pageBreakAfterRows: item.pageBreakAfterRows,
        lastPageRowPerPage: item.lastPageRowPerPage,
        rowsOnFirstPageOnly: item.rowsOnFirstPageOnly === true,
      })
      let offset = 0
      return (
        <div className="table">
          <div className="table-body">
            {item.showHeader && item.showHeader === true && (
              <div style={{ height: height + 'px' }}>
                {columns.map((citem, index) => {
                  let pItem =
                    item.fieldVariable.props && item.fieldVariable.props[citem]
                      ? item.fieldVariable.props[citem]
                      : {
                          id: index,
                          left: boxWidth * index,
                          top: height * 0 ,
                          width: boxWidth,
                          type: 'text',
                        }
                  pItem = Object.assign(
                    { id: index, left: boxWidth * index, width: boxWidth },
                    pItem,
                    { top: pItem.top, type: 'text' }
                  )
                  const { left, top, width } = pItem
                  return (
                    <div
                      key={index}
                      style={{ ...Object.assign({}, style), left, top, width }}
                      className={'box box-advance-header'}
                    >
                      {citem}
                    </div>
                  )
                })}
              </div>
            )}            
            {item?.value?.reduce((rresult, ritem, rindex) => {
              abStyle = Object.assign({}, abStyle, {
                height: height + 'px',
                left: 0,
                position: 'relative',
                width: '100%',
              })

              //rindex = (item.showHeader && item.showHeader===true)?rindex+1:rindex    
              let hasGroupDisplay = false 
              let showGroup = false       
              columns.forEach((citem, index) => {
                let itemProps = item.fieldVariable?.props[citem]
                if(itemProps?.groupDisplay && itemProps?.groupDisplay===true){
                  //firstGroup show
                  //Previous key current
                  const value = ritem[citem] //Current
                  const preVal = rindex>0?item.value[rindex-1][citem]:null
                  if(value!==preVal){
                    //Show
                    abStyle.height = ((height*2)-5) +'px'   
                    showGroup = true                 
                  }
                  hasGroupDisplay = true
                }
              })
              const content = (
                <div key={rindex} style={abStyle} >
                  {columns.map((citem, index) => {
                    let pItem =
                      item.fieldVariable.props && item.fieldVariable.props[citem]
                        ? item.fieldVariable.props[citem]
                        : {
                            id: index,
                            left: boxWidth * index,
                            top: height * rindex,
                            width: boxWidth,
                            title: ritem[citem],
                            value: ritem[citem],
                            type: 'text',
                          }
                    pItem = Object.assign(
                      {
                        id: index,
                        left: boxWidth * index,
                        width: boxWidth,
                        title: ritem[citem],
                      },
                      pItem,
                      { top: pItem.top, value: ritem[citem], type: 'text' }
                    )
                    const {
                      left,
                      top,
                      width,
                      height,
                      textAlign,
                      textClass,
                      fontSize,
                      fontWeight,
                    } = pItem

                    let advStyle = {
                      ...Object.assign({}, style),
                      left,
                      top,
                      width,
                      height,
                      textAlign,
                      textClass,
                      fontSize,
                      fontWeight,
                      ...(hasMultilineValue(ritem[citem]) ? { whiteSpace: 'pre-wrap' } : {}),
                    }

                    let advStyleFromLayouts = Object.assign(
                      advStyle,
                      styleFromLayout
                    )

                    let itemProps = item.fieldVariable?.props[citem]
                    if(itemProps?.groupDisplay && itemProps?.groupDisplay===true){
                       
                       if(!showGroup){
                        return null
                       }else{                          
                          if(itemProps.fontWeight && itemProps.fontWeight!==''){
                            advStyle.fontWeight = itemProps?.fontWeight
                            advStyleFromLayouts.fontWeight = itemProps?.fontWeight
                          }else if(itemProps.fontSize && itemProps.fontSize!==''){
                            advStyle.fontSize = itemProps?.fontSize
                            advStyleFromLayouts.fontSize = itemProps?.fontSize
                          }
                       }
                    }
                    if(hasGroupDisplay && !showGroup){
                      advStyle.top = 0
                      advStyleFromLayouts.top = 0
                    }else{
                      //advStyleFromLayouts.fontWeight = itemProps.fontWeight 
                      
                    }
                    

                    return (
                      <div
                        key={index}
                        style={
                          isStyleFromLayout ? advStyleFromLayouts : advStyle
                        }
                        className={'box box-advance-body '+(itemProps.textClass?itemProps.textClass:'')}
                      >
                        {itemProps.resType === 'qrcode' ? (
                          <QRCode
                            value={ritem[citem]}
                            size={roughScale(itemProps.width, 10)}
                          />
                        ) : itemProps.resType === 'barcode' ? (
                          <Barcode
                            value={ritem[citem]}
                            width={roughScale(itemProps.width, 10)}
                            height={roughScale(itemProps.height, 10)}
                          />
                        ) : (
                          <>{(itemProps.isCurrency && itemProps.isCurrency===true) ?convertCurrency(ritem[citem]):safeUnescapeNewlines(ritem[citem])}</>
                        )}
                      </div>
                    )
                  })}
                </div>
              )
              if (item.rowPerPage && item.rowPerPage > 0) {
                if (
                  rindex >= pageSlice.start &&
                  rindex < pageSlice.end
                )
                  rresult.push(content)
              } else {
                rresult.push(content)
              }
              return rresult
            }, [])}
          </div>
        </div>
      )
    } else if (item.type == 'table') {
      const columns =
        item.fieldVariable && item.fieldVariable.columns ? item.fieldVariable.columns : []
      const noOfColumn = columns ? columns.length : 4
      return (
        <table className="table-dynamic-report">
          <thead>
            <tr>
              {columns
                ? columns.map((citem, index) => (
                    <td>
                      {item.fieldVariable.props && item.fieldVariable.props[citem]
                        ? item.fieldVariable.props[citem].title
                        : citem}
                    </td>
                  ))
                : new Array(noOfColumn)
                    .fill('')
                    .map((item, index) => <td>Column{index}</td>)}
            </tr>
          </thead>
          <tbody>
            {item.value && Array.isArray(item.value)
              ? item.value.map((ritem, rindex) => (
                  <tr>
                    {item.columns.map((citem, index) => (
                      <td>{ritem[citem]}</td>
                    ))}
                  </tr>
                ))
              : new Array(4).fill('').map((item, rindex) => (
                  <tr>
                    {new Array(noOfColumn).fill('').map((item, cindex) => (
                      <td>
                        {rindex} - {cindex}
                      </td>
                    ))}
                  </tr>
                ))}
          </tbody>
        </table>
      )
    } else if (item.type === 'texteditor') {
      const textEditorSourceCode = renderTextEditorSourceCode(
        item.sourceCode,
        dataSourceList,
        {
          keepMissingTokens: !disabled,
          pageNo,
          totalPage,
          repeatPageNo: pageNoOfLayoutPage,
          repeatTotalPage: totalPageOfLayoutPage,
          repeatRowPerPage: item.rowPerPage,
          repeatPageBreakAfterRows: item.pageBreakAfterRows,
          repeatLastPageRowPerPage: item.lastPageRowPerPage,
          repeatRowsOnFirstPageOnly: item.rowsOnFirstPageOnly === true,
          fillEmptyRows: item.fillEmptyRows === true,
        },
      )

      return (
        <div className="tinymce">
          {item.styleData ? (
            <style data-classic-report-style={item.id || item.name}>
              {item.styleData}
            </style>
          ) : null}
          {textEditorSourceCode ? parse(textEditorSourceCode) : 'Text Editor'}
        </div>
      )
    } else if (item.type === 'radiogroup') {
      return (
        <div>
          <FormGroup row>
            {item.choices.map((vitem, vindex) => (
              <Checkbox
                disabled={true}
                icon={<FeatherIcon
                  size="12"
                  icon="check"
                  className="vx-icon"            
                />}
                size={14}
                borderRadius={10}
                borderWidth={1}
                checked={vitem.value === item.value}
                borderColor="black"
                style={{ cursor: 'pointer' }}
                labelStyle={{
                  marginLeft: 5,
                  marginRight: 5,
                  fontSize: '14px',
                }}
                label={vitem.text}
              />
            ))}

            <div className="ml-2 my-auto">{item?.comment}</div>
          </FormGroup>
        </div>
      )
    } else if (item.type === 'check_box') {
      return (
        <div>
          {/* <Checkbox
            disabled={true}
            icon={<FeatherIcon
              size={item.size}
              icon="check"
              className="vx-icon"            
            />}
            size={item.size}
            borderRadius={0}
            borderWidth={1}
            checked={item.value === true ? true : false}
            borderColor="black"
            style={{ cursor: 'pointer' }}
            labelStyle={{
              marginLeft: 5,
              marginRight: 5,
              fontSize: item.fontSize,
              fontWeight: item.fontWeight,
            }}
            label={item.title}
          /> */}
            <FormControlLabel
              control={
                <Checkbox
                  disabled={false}
                  checked={item.value === true ? true : false}
                  style={{ cursor: 'pointer' }}
                  color="primary"
                />
              }
              label={item.title}
              sx={{
                '& .MuiFormControlLabel-label': {
                  fontSize: item?.fontSize || '1rem',
                  fontWeight: item?.fontWeight || 'regular',
                },
                '&.MuiCheckbox-root': {
                  border: '1px solid black',
                  borderRadius: '4px', // Optional: เพิ่มความโค้งมุม
                },
                '&.Mui-checked': {
                  border: '1px solid black',
                },
              }}
            />
        </div>
      )
    } else if (item.type === 'get_api') {
      return (
        <div style={{ whiteSpace: 'pre-wrap' }}>
          {item.value === 'Get From API' ? item.name : item.value}
        </div>
      )
    } else if (item.type === 'vital_sign') {
      let props = item?.propsList && (pageNo <= item?.propsList?.length)  ? item.propsList[pageNo-1]: {}
      return (<MonitorChart showInput={false} {...props}/>)      
    } else if (item.type === 'report_resource') {
      return children
    } else {
      if(item.isCurrency && item.isCurrency===true){
        return <div style={{ whiteSpace: 'pre-wrap' }}>{convertCurrency(item.value)}</div>
      }
      return <div style={{ whiteSpace: 'pre-wrap' }}>{item.value}</div>
    }
  }

  if (item.type === 'pdf' && item.fullPage) {
    left = 0
    top = 0
    width = '100%'
    height = '100%'
  }

  if (
    reportHasMultiplePages &&
    currentPageIsLast &&
    item.lastPageTop !== undefined &&
    item.lastPageTop !== null &&
    item.lastPageTop !== '' &&
    typeof item.lastPageTop !== 'boolean'
  ) {
    top = toCssSize(item.lastPageTop)
  }

  width = toCssSize(width)
  height = toCssSize(height)
  const isTextItem = ['text', 'object', 'textformat', 'get_api'].includes(
    item.type,
  )
  let bxStyle = Object.assign(
    {
      left: left,
      top: top,
      width: width,
      height: height,
      fontSize: item.fontSize,
      lineHeight: item.lineHeight,
      fontWeight: item.fontWeight,
      fontFamily: pdfOverlayMode
        ? REPORT_FONT_FAMILY[item.fontFamily] || item.fontFamily
        : undefined,
      fontStyle: pdfOverlayMode ? item.fontStyle : undefined,
      textAlign: item.textAlign,
      color: pdfOverlayMode ? item.textColor : undefined,
      whiteSpace: isTextItem ? 'pre-wrap' : undefined,
      overflowWrap: isTextItem ? 'break-word' : undefined,
      zIndex: item.zIndex || (isBackground ? 0 : 1),
      overflow: ['image', 'pdf', 'report_resource'].includes(item.type)
        ? 'hidden'
        : undefined,
    },
    style,
    bg,
    showBorder && !isBackground ? {border:'1px dashed lightgray'}:{}
  )  
  if (disabled || isLocked) {
    bxStyle.cursor = 'default'
  }

  let bxStyleFromLayouts = Object.assign(bxStyle, styleFromLayout)
  if (pdfOverlayMode) {
    if (item.fontWeight) bxStyleFromLayouts.fontWeight = item.fontWeight
    if (item.textColor) bxStyleFromLayouts.color = item.textColor
  }

  let boxItemClass = item?.type?'box-'+item?.type:'box-unknown-type'

  return (
    <div
      ref={disabled || isLocked ? null : drag}
      style={isStyleFromLayout ? bxStyleFromLayouts : bxStyle}
      role="Box"
      data-box-id={id}
      data-box-background={isBackground ? 'true' : 'false'}
      data-box-locked={isLocked ? 'true' : 'false'}
      data-report-designer-placeholder={
        designerTextPlaceholder ? 'true' : undefined
      }
      data-pdf-overlay-placeholder={
        pdfOverlayMode && designerTextPlaceholder ? 'true' : undefined
      }
      title={
        designerTextPlaceholder
          ? 'Datasource: ' + designerTextPlaceholder
          : undefined
      }
      className={'box '+boxItemClass+" "+(item.textClass?item.textClass:'')+(isBackground?' background-item':'')+(isLocked?' locked-item':'')+(activeBackground?' active-item':'')+(multiSelected?' multi-selected-item':'')+(pdfOverlayMode && item.type === 'texteditor'?' pdf-overlay-text-style':'')}
      onClick={(event) => {
        event.stopPropagation()
        onClick(item, event)
      }}
      onDoubleClick={()=>{onDoubleClick(item)}}
    >
      {renderChildren()}
    </div>
  )
}
